import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { btnGold, btnRed } from "@/components/nds/windows/finance/parts";

const key = (x) => `${x.resident_id}:${x.week}`;

function DiffRow({ x, on, onToggle }) {
  const done = !!x.decision;
  return (
    <tr className={`border-t border-white/5 ${done ? "opacity-50" : ""}`} data-testid={`ledger-diff-row-${key(x)}`}>
      <td className="py-1 pr-2">{!done && <input type="checkbox" checked={on} onChange={onToggle} data-testid={`ledger-diff-check-${key(x)}`} className="accent-[#c9a961]" />}</td>
      <td className="pr-2 text-nex-silver">{x.resident}{x.status !== "active" && <span className="text-nex-muted"> · {x.status}</span>}</td>
      <td className="pr-2 font-mono">{x.week}</td>
      <td className="pr-2 font-mono">{money(x.old_rent)} → {money(x.new_rent)}</td>
      <td className="pr-2 font-mono">{money(x.old_cm)} → {money(x.new_cm)}</td>
      <td className="pr-2 text-nex-muted">{x.old_source || "—"}</td>
      <td className="text-nex-muted">{done ? `${x.decision.decision === "post" ? "Posted" : "Left as ledger"} · ${x.decision.by?.name || ""}` : x.rent_gap + x.cm_gap > 0 ? "Open" : "Open · ledger higher or corrected · leave only"}</td>
    </tr>
  );
}

/** Super admin: every resident-week (from Oct 1, 2026) where the old board's Rent / PACT Fee differs from the ledger. Nothing posts until you confirm. */
export function LedgerDiff() {
  const sa = useIsSuper();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!sa) return null;
  const load = async () => { setBusy(true); try { setD(await finApi.get("/ledger-diff")); setSel({}); } catch (e) { toast.error(finErr(e)); } setBusy(false); };
  const rows = (d?.rows || []).filter((x) => sel[key(x)]);
  const decide = async (decision) => {
    setBusy(true);
    try {
      const body = { rows: rows.map((x) => ({ resident_id: x.resident_id, week: x.week })), decision, note };
      const p = await finApi.post("/ledger-diff/decide", { ...body, confirm: false });
      const what = decision === "post" ? `Post Rent ${money(p.rent)} and PACT Fee ${money(p.cm)} to the ledger for ${p.rows} week(s)?` : `Leave ${p.rows} week(s) as the ledger shows? The missing amounts will roll to the Line of Credit.`;
      if (window.confirm(what)) { const r = await finApi.post("/ledger-diff/decide", { ...body, confirm: true }); toast.success(`${r.decided} week(s) decided`); await load(); }
    } catch (e) { toast.error(finErr(e)); }
    setBusy(false);
  };
  const open = (d?.rows || []).filter((x) => !x.decision);
  return (
    <div data-testid="ledger-diff" className="p-3 rounded-md border border-white/10 bg-white/[0.02] space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="nds-label-dim text-[9px]">Old board vs ledger · from Oct 1, 2026 · super admin</span>
        <button type="button" disabled={busy} onClick={load} data-testid="ledger-diff-load" className={btnGold}>{busy && !d ? "Checking…" : d ? "Reload" : "Show differences"}</button>
        {d && <span className="text-nex-silver" data-testid="ledger-diff-summary">{d.open} open · Rent {money(d.open_rent_gap)} · PACT Fee {money(d.open_cm_gap)} the ledger lacks</span>}
      </div>
      {d && <>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setSel(Object.fromEntries(open.map((x) => [key(x), true])))} data-testid="ledger-diff-select-all" className="text-nex-gold hover:underline">Tick all open</button>
          <button type="button" onClick={() => setSel({})} data-testid="ledger-diff-select-none" className="text-nex-muted hover:underline">Clear</button>
          <span className="text-nex-muted" data-testid="ledger-diff-selected">{rows.length} ticked · Rent {money(rows.reduce((a, x) => a + x.rent_gap, 0))} · PACT Fee {money(rows.reduce((a, x) => a + x.cm_gap, 0))}</span>
        </div>
        <div className="max-h-80 overflow-auto"><table className="w-full text-left text-[11px]"><thead className="text-nex-muted"><tr><th /><th>Resident</th><th>Week</th><th>Rent old → ledger</th><th>PACT Fee old → ledger</th><th>Old source</th><th>Decision</th></tr></thead>
          <tbody>{d.rows.map((x) => <DiffRow key={key(x)} x={x} on={!!sel[key(x)]} onToggle={() => setSel((s) => ({ ...s, [key(x)]: !s[key(x)] }))} />)}</tbody></table></div>
        {rows.length > 0 && <div className="flex flex-wrap items-center gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (required)" data-testid="ledger-diff-note" className="flex-1 min-w-[200px] bg-black/30 border border-white/10 rounded-sm px-2 py-1" />
          <button type="button" disabled={busy || note.replace(/[^A-Za-z]/g, "").length < 5} onClick={() => decide("post")} data-testid="ledger-diff-post" className={btnGold}>Post to ledger</button>
          <button type="button" disabled={busy || note.replace(/[^A-Za-z]/g, "").length < 5} onClick={() => decide("leave")} data-testid="ledger-diff-leave" className={btnRed}>Leave as ledger</button>
        </div>}
      </>}
    </div>
  );
}
